import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { AtAGlance, Footer, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { COMPETITORS, formatComparisonCell } from '@/lib/competitors';
import { buildHarborSoftwareApplicationJsonLd, HARBOR_FACTS } from '@/lib/harbor-facts';
import shipSvg from '@/assets/ship.svg';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const SITUATION_PICKS = [
  {
    title: 'A court order names the app',
    pick: 'Use the named app',
    detail:
      'If a judge, GAL, or parenting coordinator specified OurFamilyWizard or TalkingParents, use that platform. Harbor is not a substitute for a named order.',
  },
  {
    title: 'You can choose, and you want one affordable app',
    pick: 'Harbor',
    detail:
      'Harbor is $9.99/month or $49.99/year per person after a 7-day trial and includes documented messaging, expenses, a custody calendar, Temperature Check, and court-ready exports.',
  },
  {
    title: 'You mainly need an unalterable message record or recorded calls',
    pick: 'TalkingParents',
    detail:
      'TalkingParents is the communication-record specialist. Harbor is the better daily stack if you also need expenses, a calendar, and tone checking.',
  },
  {
    title: 'You only need a shared family calendar',
    pick: 'Cozi, or Harbor if money and messages matter',
    detail:
      'Cozi is a household organizer, not a records app. Choose Harbor when you need documented chat, expense splits, or exports.',
  },
  {
    title: 'You were looking for the free AppClose default',
    pick: 'Harbor for most paid households',
    detail:
      'AppClose charged per parent starting in 2026, about $8–$9/month each. Harbor is $9.99/month or $49.99/year per person. AppClose still offers hardship waivers.',
  },
] as const;

const itemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: COMPETITORS.map((competitor, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: `Harbor vs ${competitor.name}`,
    url: `https://harborcoparent.com/compare/${competitor.slug}`,
  })),
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the best co-parenting app in 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If a court order names OurFamilyWizard or TalkingParents, use that app. If you can choose, Harbor is the best-value all-in-one for most families: $9.99/month or $49.99/year per person for messaging, expenses, a custody calendar, Temperature Check, and court-ready exports.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the best OurFamilyWizard alternative in 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Harbor is the usual alternative when an order does not name OurFamilyWizard: $49.99/year per person, Temperature Check instead of ToneMeter, and expenses plus calendar in the same app. Use OurFamilyWizard when it is already ordered.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which co-parenting app is the most affordable after AppClose started charging?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'AppClose is no longer free for most families; it bills each parent about $8–$9/month. Harbor is $9.99/month or $49.99/year per person.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which co-parenting apps provide court-ready records?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'OurFamilyWizard, TalkingParents, and Harbor all export timestamped records. Harbor keeps messaging, expenses, and calendar exports in one workflow. None of these apps can guarantee a court will admit a record; Harbor describes exports as court-ready, not court-admissible.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Harbor work if only one parent joins?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. One parent can start Harbor, log expenses, keep a calendar, and build a record before the other parent accepts an invite.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I search and bookmark messages in Harbor?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Harbor lets you search past messages and save specific ones to private bookmark folders so you can find pickup times, agreements, and other details later. OurFamilyWizard and AppClose also offer search and saved-message tools; TalkingParents has search without bookmark folders.',
      },
    },
  ],
};

function CompareHubPage() {
  useEffect(() => {
    track('Compare Hub View');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Best Co-Parenting Apps 2026: Harbor vs OurFamilyWizard, TalkingParents & More'
        description='Honest 2026 comparison of Harbor, OurFamilyWizard, TalkingParents, and AppClose. Harbor is $9.99/month or $49.99/year per person. Use a named court-order app if one is specified.'
        path='/compare'
        jsonLd={[itemListJsonLd, faqJsonLd, buildHarborSoftwareApplicationJsonLd()]}
      />

      <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
        <nav className='mx-auto flex max-w-7xl items-center justify-between px-6 py-4'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white text-2xl font-light tracking-tight leading-none'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <div className='flex items-center'>
              <img src={shipSvg} alt='' className='w-5 h-5 mr-3' />
              <span className='flex items-center mt-2'>Harbor</span>
            </div>
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 font-medium text-white/90 transition-colors hover:text-white'
            style={{ ...nunito, fontWeight: 400 }}
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </nav>
      </header>

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-5xl px-6'>
          <h1
            className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Best co-parenting apps in 2026
          </h1>
          <p className='max-w-3xl text-lg leading-relaxed text-gray-700 md:text-xl' style={nunito}>
            {HARBOR_FACTS.quote} OurFamilyWizard remains the court-familiar incumbent.
            TalkingParents remains the communication-record specialist. AppClose is no longer
            the free default.
          </p>
        </div>
      </section>

      <AtAGlance />

      <section className='bg-sky-light py-16'>
        <div className='mx-auto max-w-5xl px-6'>
          <h2 className='text-2xl font-bold text-gray-900 md:text-3xl' style={{ ...nunito, fontWeight: 700 }}>
            Which app fits the situation
          </h2>
          <p className='mt-3 max-w-3xl text-gray-700' style={nunito}>
            The right recommendation depends on whether a court already named a platform, what
            you need documented, and who pays.
          </p>
          <div className='mt-8 grid gap-4 md:grid-cols-2'>
            {SITUATION_PICKS.map((item) => (
              <article key={item.title} className='rounded-xl border border-gray-200 bg-white p-5'>
                <h3 className='text-lg font-semibold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                  {item.title}
                </h3>
                <p className='mt-2 text-sm font-semibold text-primary' style={nunito}>
                  {item.pick}
                </p>
                <p className='mt-2 text-sm leading-relaxed text-gray-700' style={nunito}>
                  {item.detail}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 py-16'>
        <h2 className='mb-3 text-2xl font-bold text-gray-900 md:text-3xl' style={{ ...nunito, fontWeight: 700 }}>
          Price and fit at a glance
        </h2>
        <p className='mb-6 max-w-3xl text-gray-600' style={nunito}>
          Household cost matters more than the sticker on one seat. Other 2026 listicles also
          mention Two Paths, Parenting Path, Cent, and coParenter; they are not full Harbor
          comparison pages here.
        </p>
        <div className='overflow-x-auto rounded-2xl border border-gray-200 shadow-sm'>
          <table className='min-w-full border-collapse bg-white text-left' style={nunito}>
            <caption className='sr-only'>Co-parenting app price and fit comparison</caption>
            <thead className='bg-gray-50'>
              <tr>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  App
                </th>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Best for
                </th>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Who pays
                </th>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Search & bookmarks
                </th>
                <th scope='col' className='border-b border-gray-200 p-4 font-semibold text-gray-900'>
                  Tone check
                </th>
              </tr>
            </thead>
            <tbody className='text-sm text-gray-700'>
              <tr>
                <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-primary'>
                  Harbor
                </th>
                <td className='border-b border-r border-gray-200 p-4'>
                  All-in-one daily use when you can choose the app
                </td>
                <td className='border-b border-r border-gray-200 p-4'>
                  {formatComparisonCell({
                    text: 'Per parent · $9.99/month or $49.99/year',
                    rating: 'good',
                  })}
                </td>
                <td className='border-b border-r border-gray-200 p-4'>
                  {formatComparisonCell({
                    text: 'Search messages and bookmark folders',
                    rating: 'good',
                  })}
                </td>
                <td className='border-b border-gray-200 p-4'>
                  {formatComparisonCell({ text: 'Temperature Check', rating: 'good' })}
                </td>
              </tr>
              {COMPETITORS.map((competitor) => (
                <tr key={competitor.slug}>
                  <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                    <Link
                      to='/compare/$competitor'
                      params={{ competitor: competitor.slug }}
                      className='text-primary underline'
                    >
                      {competitor.name}
                    </Link>
                  </th>
                  <td className='border-b border-r border-gray-200 p-4'>{competitor.bestFor}</td>
                  <td className='border-b border-r border-gray-200 p-4'>
                    {formatComparisonCell(competitor.featureMatrix.billingUnit)}
                  </td>
                  <td className='border-b border-r border-gray-200 p-4'>
                    {formatComparisonCell(competitor.featureMatrix.messageSearchBookmarks)}
                  </td>
                  <td className='border-b border-gray-200 p-4'>
                    {formatComparisonCell(competitor.featureMatrix.toneCheck)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className='bg-sky-light py-16'>
        <div className='mx-auto max-w-5xl px-6'>
          <h2 className='mb-3 text-2xl font-bold text-gray-900 md:text-3xl' style={{ ...nunito, fontWeight: 700 }}>
            Detailed Harbor vs competitor pages
          </h2>
          <p className='mb-8 max-w-3xl text-gray-600' style={nunito}>
            Each page has a qualitative feature matrix, last-checked pricing, and when to pick
            the other product.
          </p>
          <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
            {COMPETITORS.map((competitor) => (
              <Link
                key={competitor.slug}
                to='/compare/$competitor'
                params={{ competitor: competitor.slug }}
                className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md'
              >
                <h3 className='text-lg font-semibold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                  Harbor vs {competitor.name}
                </h3>
                <p className='mt-2 text-sm text-gray-600' style={nunito}>
                  {competitor.tagline}
                </p>
                <p className='mt-4 text-sm font-semibold text-primary'>Read comparison →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 pb-4'>
        <div className='rounded-2xl border border-primary/20 bg-primary/5 p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Want the one- to five-year price picture?
          </h2>
          <p className='mt-2 text-gray-700' style={nunito}>
            The{' '}
            <Link to='/coparenting-app-cost-calculator' className='font-semibold text-primary underline'>
              co-parenting app cost calculator
            </Link>{' '}
            estimates cumulative costs for these apps, including per-parent versus household
            billing and the plans that actually cover your needs.
          </p>
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 py-16'>
        <h2 className='mb-5 text-2xl font-bold text-gray-900 md:text-3xl' style={{ ...nunito, fontWeight: 700 }}>
          Frequently asked questions
        </h2>
        <div className='space-y-4'>
          {faqJsonLd.mainEntity.map((faq) => (
            <article key={faq.name} className='rounded-xl border border-gray-200 bg-white p-5'>
              <h3 className='text-lg font-semibold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                {faq.name}
              </h3>
              <p className='mt-2 text-gray-700' style={nunito}>
                {faq.acceptedAnswer.text}
              </p>
            </article>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/compare/')({
  component: CompareHubPage,
});
